<template>
  <div>
    <h3 class="text-subtitle-1 font-weight-bold mb-4 d-flex align-center ga-2">
      <v-icon
        size="18"
        color="primary"
      >
        mdi-palette
      </v-icon>
      {{ $t('onboarding.wizard.data-entry.brand-colors') }}
    </h3>

    <v-row class="mb-6">
      <v-col
        v-for="row in colorRows"
        :key="row.field"
        cols="12"
        md="6"
        :class="{ 'onboarding-color-row--error': Boolean(visibleFieldError(row.field)) }"
        @focusout="markFieldTouched(row.field)"
      >
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center ga-2">
            <span class="text-body-2 font-weight-medium">
              {{ $t(row.labelKey) }}
              <span
                v-if="isFieldRequired(row.field)"
                class="text-error"
                aria-hidden="true"
                >*</span
              >
            </span>
            <span class="onboarding-inline-badge-slot">
              <AiBadge
                v-if="aiCapturedFields.has(row.field)"
                :label="$t('onboarding.wizard.data-entry.ai-captured')"
              />
            </span>
          </div>
          <v-btn
            variant="text"
            size="small"
            color="primary"
            class="text-caption"
            @click="openColorDialog(row.which)"
          >
            <v-icon
              start
              size="14"
            >
              mdi-auto-fix
            </v-icon>
            {{ $t('onboarding.wizard.data-entry.set-color') }}
          </v-btn>
        </div>
        <div class="d-flex align-start ga-2">
          <button
            type="button"
            class="color-swatch"
            :class="{
              'color-swatch--empty': !hasMeaningfulOnboardingValue(row.field, formData[row.field]),
            }"
            :style="colorSwatchStyle(row.field, formData[row.field])"
            :aria-label="$t(row.labelKey)"
            @click="openColorDialog(row.which)"
          />
          <v-text-field
            :model-value="displayValue(row.field)"
            :id="`onboarding-field-${row.field}`"
            :data-ai-field="row.field"
            :class="fieldClass(row.field)"
            :error-messages="fieldErrorMessage(row.field)"
            variant="outlined"
            density="compact"
            rounded="lg"
            hide-details="auto"
            @focus="onFieldFocus(row.field)"
            @keydown.esc="cancelTextDraft(row.field)"
            @update:model-value="onTextInput(row.field, $event)"
          />
        </div>
        <!-- The color's own format/contrast error (on the input) takes precedence. -->
        <div
          v-if="visibleFieldError(row.field) && !fieldErrorMessage(row.field).length"
          class="text-caption text-error mt-1"
          role="alert"
        >
          {{ visibleFieldError(row.field) }}
        </div>
      </v-col>
    </v-row>

    <ColorPickerDialog
      v-model="colorDialogOpen"
      :title="colorDialogTitle"
      :draft-color="draftColor"
      :error-message="dialogErrorMessage"
      :save-disabled="!!dialogErrorMessage"
      @update:draft-color="draftColor = $event"
      @cancel="colorDialogOpen = false"
      @save="applyColorDialog"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  hasMeaningfulOnboardingValue,
  ONBOARDING_COLOR_PICKER_SEED,
} from '@/lib/ai/onboardingFieldValues'
import { getBrandColorIssue } from '@/modules/onboarding/lib/brandColorValidation'

import AiBadge from '../../AiBadge.vue'
import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import ColorPickerDialog from './ColorPickerDialog.vue'

type ColorWhich = 'primary' | 'secondary'
type ColorField = 'primaryColor' | 'secondaryColor'

const colorRows: { which: ColorWhich; field: ColorField; labelKey: string }[] = [
  {
    which: 'primary',
    field: 'primaryColor',
    labelKey: 'onboarding.wizard.data-entry.primary-color',
  },
  {
    which: 'secondary',
    field: 'secondaryColor',
    labelKey: 'onboarding.wizard.data-entry.secondary-color',
  },
]

const {
  formData,
  aiCapturedFields,
  updateField,
  fieldClass,
  onFieldFocus,
  visibleFieldError,
  markFieldTouched,
  isFieldRequired,
  brandColorDrafts: textDrafts,
} = useOnboardingDataEntryContext()
const { t } = useI18n()

const fieldForWhich = (which: ColorWhich): ColorField =>
  which === 'primary' ? 'primaryColor' : 'secondaryColor'

/**
 * Returns an i18n key if `value` must not be stored for `field`, otherwise null.
 * An empty value is allowed: it means "not set" and the default theme applies.
 */
function colorValidationIssue(field: ColorField, value: string | null | undefined): string | null {
  return getBrandColorIssue(field, value)
}

// ---- Text field --------------------------------------------------------------
// Typed input is kept as a draft and only committed to formData once it is a valid,
// accessible color, so the stored value can never be a rejected one. An invalid draft
// stays visible with its error until it is corrected, canceled with Escape, or replaced
// from the picker. OnboardingDataEntry blocks Continue while one is left.

const displayValue = (field: ColorField) => textDrafts[field] ?? formData.value[field]

function onTextInput(field: ColorField, value: string) {
  if (colorValidationIssue(field, value)) {
    textDrafts[field] = value
    return
  }
  textDrafts[field] = null
  updateField(field, value.trim())
}

function cancelTextDraft(field: ColorField) {
  textDrafts[field] = null
}

// Also flags stored values that never went through this UI (AI capture, older data).
function fieldErrorMessage(field: ColorField): string[] {
  const issue = colorValidationIssue(field, displayValue(field))
  return issue ? [t(issue)] : []
}

// ---- Swatch ------------------------------------------------------------------

function colorSwatchStyle(field: ColorField, value: string) {
  if (!hasMeaningfulOnboardingValue(field, value)) return {}
  return { backgroundColor: value.trim() }
}

// ---- Picker dialog -----------------------------------------------------------

const colorDialogOpen = ref(false)
const colorDialogTarget = ref<ColorWhich | null>(null)
const draftColor = ref(ONBOARDING_COLOR_PICKER_SEED)

const colorDialogTitle = computed(() =>
  colorDialogTarget.value === 'primary'
    ? t('onboarding.wizard.data-entry.primary-color')
    : t('onboarding.wizard.data-entry.secondary-color')
)

const dialogErrorMessage = computed(() => {
  if (!colorDialogTarget.value) return null
  const issue = colorValidationIssue(fieldForWhich(colorDialogTarget.value), draftColor.value)
  return issue ? t(issue) : null
})

const openColorDialog = (which: ColorWhich) => {
  colorDialogTarget.value = which
  const field = fieldForWhich(which)
  const current = formData.value[field]
  draftColor.value = hasMeaningfulOnboardingValue(field, current)
    ? current.trim()
    : ONBOARDING_COLOR_PICKER_SEED
  colorDialogOpen.value = true
}

const applyColorDialog = () => {
  if (!colorDialogTarget.value) return
  // Defensive: the dialog's save button is disabled, but never trust that alone.
  if (dialogErrorMessage.value) return

  const field = fieldForWhich(colorDialogTarget.value)
  textDrafts[field] = null
  updateField(field, draftColor.value)
  colorDialogOpen.value = false
}
</script>

<style scoped>
.onboarding-color-row--error :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  color: rgb(var(--v-theme-error));
}

.onboarding-inline-badge-slot {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
}

.color-swatch {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 2px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
  padding: 0;
  cursor: pointer;
  appearance: none;
}

.color-swatch--empty {
  background: repeating-linear-gradient(-45deg, #e8e8e8, #e8e8e8 6px, #f5f5f5 6px, #f5f5f5 12px);
}

.color-swatch:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
